import './libraries/icon_library.js';
import './libraries/translation.js';
import './components/icon.js';
import './libraries/theme.css';

import { type PathRouteConfig, Router } from '@lit-labs/router';
import { css, html, LitElement, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { type IAreaViews, listAreaViews } from './libraries/area_views.js';
import { clearLocalTokens, isAuthenticated, refreshAuthentication } from './libraries/auth.js';
import type { DetailMode } from './libraries/detail_mode.js';
import { hydrateDisplaySettings } from './libraries/display_settings.js';
import { setDocumentTitle } from './libraries/document_title.js';
import { isDevelopment } from './libraries/environment.js';
import {
    type FertilizerApplicationView,
} from './libraries/fertilizer_application_view.js';
import {
    type FertilizerProductView,
} from './libraries/fertilizer_product_view.js';
import {
    cultivationDataAreaSlug,
    documentationAreaSlug,
    fertilizerPlanAreaSlug,
    fertilizersAreaSlug,
    fertilizerYearAreas,
    type IFertilizerYearArea,
    livestockSubAreaSlug,
    mineralFertilizationSubAreaSlug,
    mineralSubAreaSlug,
    nitrogenAdjustmentAreaSlug,
    nutrientComparisonAreaSlug,
    organicFertilizationSubAreaSlug,
    organicFertilizersSubAreaSlug,
    organicSubAreaSlug,
    parcelsAreaSlug,
    previousCropsSubAreaSlug,
    previousYearAreaSlug,
    soilSamplesAreaSlug,
    yieldsAreaSlug,
    yieldSubAreaSlug,
} from './libraries/fertilizer_year_areas.js';
import { getDefaultAuthenticatedPath } from './libraries/landing_path.js';
import { redirectTo, redirectToReplacing, rememberReturnPath } from './libraries/navigation.js';
import type { OrganicFertilizerDeliveryView } from './libraries/organic_fertilizer_delivery_view.js';
import type { ParcelView } from './libraries/parcel_view.js';
import { isAdmin } from './libraries/permissions.js';
import {
    type PreviousYearTab,
} from './libraries/previous_year_view.js';
import { installSessionExpiryHandler } from './libraries/session.js';
import { installUrlPatternPolyfill } from './libraries/url_pattern_polyfill.js';
import type { YieldSeriesView } from './libraries/yield_series_view.js';
import type { AccountMode } from './pages/account.js';
import type { AdminTab } from './pages/admin.js';
import type { FarmDetailTab } from './pages/farm_detail.js';
import type { OrganizationDetailTab } from './pages/organization_detail.js';

installUrlPatternPolyfill();
installSessionExpiryHandler();

/**
 * One route, and the name of the view it renders.
 *
 * `title` is required, so a route added without one does not compile. That is the
 * whole point of the type: SC 2.4.2 asks every route of the SPA to name itself, which
 * the single static title the server sends cannot do, and a rule that is only written
 * down goes unfollowed by the next route added.
 *
 * `null` says the route renders nothing to name, which is true of the three that
 * only redirect.
 */
interface IAppRoute extends PathRouteConfig {
    title: string | ((params: Record<string, string | undefined>) => string) | null;
}

/**
 * One route with its title written on entry.
 *
 * `enter` rather than `render`, because a title belongs to a navigation while
 * `render` runs again on every update of the host. A guard that refuses admits
 * nothing and writes nothing, so the route it redirects to is what names the
 * document, and the title never describes a view the user was not let into.
 * @param route - The declared route.
 * @returns The route the router is handed, without the declared title, which is
 * `app.ts`'s own vocabulary rather than the router's.
 */
function titled (route: IAppRoute): PathRouteConfig {
    const {
        title, ...config
    } = route;

    return {
        ...config,
        enter: async (params): Promise<boolean> => {
            const admitted = await config.enter?.(params) ?? true;

            if (admitted && title !== null) {
                setDocumentTitle(typeof title === 'string' ? title : title(params));
            }

            return admitted;
        },
    };
}

/**
 * Loads the public-shell bundle. Repeated `import()` of one specifier is deduped
 * by the module loader, so a guard can reload a bundle without memoisation.
 */
async function loadPublicShellBundle (): Promise<void> {
    await import('./components/public_shell_bundle.js');
}

/**
 * Loads the application-frame bundle. The entry HTML preloads it, so a guard's
 * `await` usually resolves from the completed preload.
 */
async function loadApplicationFrameBundle (): Promise<void> {
    await import('./components/application_frame_bundle.js');
}

/**
 * Paths that do not require authentication.
 * Unauthenticated users may visit these without being redirected to /login.
 * The unauthenticated 404 fallback is also a public surface but is matched
 * separately at render-time via `isAuthenticated()` because it has no
 * fixed pathname.
 */
const publicPaths = new Set([
    '/login',
    '/login/verified',
    '/login/already-verified',
    '/login/email-changed',
    '/registration',
    '/registration/link-expired',
    '/registration/link-invalid',
    '/registration/link-used',
    '/registration/verification-failed',
    '/reset-password',
    '/new-password',
]);

/**
 * Auth guard for protected routes.
 * Attempts a token refresh when the access token is expired before redirecting.
 * @returns Whether the user is allowed to enter the route.
 */
async function requireAuth (): Promise<boolean> {
    try {
        if (await refreshAuthentication()) {
            await loadApplicationFrameBundle();

            return true;
        }
    } catch {

        // Network error — treat as unauthenticated.
    }

    rememberReturnPath(window.location.pathname + window.location.search);
    redirectTo('/login');

    return false;
}

/**
 * The guard of the routes that only stand for the landing: a signed-in user is sent on to
 * the default view, and neither route renders anything of its own.
 * @returns Always false; the route is never entered.
 */
async function enterDefaultPath (): Promise<boolean> {
    if (await requireAuth()) {
        redirectTo(await getDefaultAuthenticatedPath());
    }

    return false;
}

/**
 * Auth + admin guard for admin-only routes (e.g. /administration).
 * Redirects non-admins to /dashboard.
 * @returns Whether the user is allowed to enter the route.
 */
async function requireAdmin (): Promise<boolean> {
    if (!await requireAuth()) {
        return false;
    }

    if (await isAdmin()) {
        return true;
    }

    redirectTo('/dashboard');

    return false;
}

/**
 * Access guard for the API documentation route. Mirrors the server-side
 * specification route: open in development, otherwise admin-only. Reusing
 * `requireAdmin` keeps the redirect-for-non-admins behaviour consistent.
 * @returns Whether the user is allowed to enter the route.
 */
async function requireApiDocs (): Promise<boolean> {
    if (isDevelopment) {
        await loadApplicationFrameBundle();

        return true;
    }

    return requireAdmin();
}

/**
 * Whether the current navigation is a development auto-login request
 * (/login?force=true). False outside development, so the guard bypass it
 * gates is dead in staging and production.
 * @returns Whether the dev force-login bypass applies.
 */
function isDevForceLoginRequest (): boolean {
    return isDevelopment &&
        window.location.pathname === '/login' &&
        new URLSearchParams(window.location.search).get('force') === 'true';
}

/**
 * Decides guest access for guest-only routes (e.g. /login), redirecting
 * authenticated users to their default authenticated path. The public-shell
 * bundle load is layered on top by {@link requireGuest}.
 * @returns Whether the user is allowed to enter the route.
 */
async function resolveGuestAccess (): Promise<boolean> {

    // Server invalidated all sessions during e-mail-change verification, but
    // the client-side access token may still be alive locally. Clear it so the
    // login page can render its success callout instead of bouncing to dashboard.
    if (window.location.pathname === '/login/email-changed') {
        clearLocalTokens();

        return true;
    }

    // Development-only auto-login override (see populate-development links and
    // login.ts). Dead outside development; the normal guest redirect applies.
    if (isDevForceLoginRequest()) {
        return true;
    }

    try {
        if (!(await refreshAuthentication())) {
            return true;
        }
    } catch {
        return true;
    }

    redirectTo(await getDefaultAuthenticatedPath());

    return false;
}

/**
 * Auth guard for guest-only routes (e.g. /login). Loads the public-shell
 * bundle before admitting a guest so the public shell and page are defined by
 * the time the route renders.
 * @returns Whether the user is allowed to enter the route.
 */
async function requireGuest (): Promise<boolean> {
    if (!(await resolveGuestAccess())) {
        return false;
    }

    await loadPublicShellBundle();

    return true;
}

/**
 * Enter hook for the public routes that carry no guest or auth check (the
 * registration and password-reset confirmation pages). Loads the public-shell
 * bundle so the shell and page are defined by render time, then always admits.
 * @returns Always true.
 */
async function enterPublicRoute (): Promise<boolean> {
    await loadPublicShellBundle();

    return true;
}

/**
 * Renders `<fm-organization-detail>` for the given tab. All three
 * organization-tab routes share this single `html` template literal so
 * lit-html's template cache reuses the same DOM element across tab
 * navigation; only the `tab` attribute updates. Without this sharing each
 * route entry would carry its own template, causing a full DOM teardown +
 * remount on every tab click — which would re-trigger `loadData()` and
 * remount the wrapping shell as well. The master-data edit route shares the
 * literal for the same reason: only the `mode` attribute updates, so the
 * leaf keeps its state and its post-save success message across the
 * transition back to the read view.
 * @param organizationId - The organization id from the route params.
 * @param tab - The tab to activate.
 * @param mode - Whether to render the master data read view or its edit form.
 * @returns The page template.
 */
function renderOrganizationDetail (
    organizationId: string,
    tab: OrganizationDetailTab,
    mode: DetailMode = 'view'
): TemplateResult {
    return html`
        <fm-organization-detail organizationId=${organizationId} tab=${tab} mode=${mode}>
        </fm-organization-detail>
    `;
}

/**
 * Renders `<fm-farm-detail>` for the given tab. All farm-tab routes share
 * this single `html` template literal so lit-html reuses the element
 * across tab navigation, both within an organization scope and across the
 * organization-scoped vs. standalone scopes. The `loadData()` cache inside
 * `<fm-farm-detail>` invalidates correctly when `organizationId` changes,
 * so cross-scope navigation still refetches as needed. The master-data edit
 * routes share the literal for the same reason: only the `mode` attribute
 * updates, so the leaf keeps its state and its post-save success message
 * across the transition back to the read view.
 * @param farmId - The farm id from the route params.
 * @param tab - The tab to activate.
 * @param organizationId - The organization id from the route params, or the
 *     empty string for standalone farm routes.
 * @param mode - Whether to render the master data read view or its edit form.
 * @returns The page template.
 */
function renderFarmDetail (
    farmId: string,
    tab: FarmDetailTab,
    organizationId: string,
    mode: DetailMode = 'view'
): TemplateResult {
    return html`
        <fm-farm-detail
            organizationId=${organizationId}
            farmId=${farmId}
            tab=${tab}
            mode=${mode}>
        </fm-farm-detail>
    `;
}

/**
 * Renders `<fm-admin>` for the given tab. Both administration-tab routes
 * share this single `html` template literal so lit-html reuses the same
 * element across tab navigation; only the `tab` attribute updates.
 * @param tab - The tab to activate.
 * @returns The page template.
 */
function renderAdmin (tab: AdminTab): TemplateResult {
    return html`<fm-admin tab=${tab}></fm-admin>`;
}

/**
 * Renders `<fm-account>` for the given mode. The view route and the two
 * `/edit` sub-routes share this single `html` template literal so lit-html
 * reuses the same element across mode transitions; only the `mode`
 * attribute updates. Element reuse preserves `<fm-account>`'s `feedback`
 * state across the redirect that follows a successful save, so the
 * success callout is rendered on the view page without a separate
 * cross-route delivery channel.
 * @param mode - The mode to activate.
 * @returns The page template.
 */
function renderAccount (mode: AccountMode): TemplateResult {
    return html`<fm-account mode=${mode}></fm-account>`;
}

/**
 * The registry entry of one area whose own routes hang under it. Read from the registry
 * rather than restated, so the slug, the label and the lead stay single-sourced. The
 * fallback stands in for an entry that cannot be missing, and says nothing under the
 * heading.
 * @param slug - The area's slug.
 * @param label - The area's label, as the registry carries it.
 * @returns The registry entry.
 */
function registeredArea (slug: string, label: string): IFertilizerYearArea {
    return fertilizerYearAreas.find(area => area.slug === slug) ?? {
        slug,
        label,
        lead: (): string | null => null,
    };
}

const parcelsArea = registeredArea(parcelsAreaSlug, 'Schlagdaten');
const soilSamplesArea = registeredArea(soilSamplesAreaSlug, 'Bodenuntersuchung');
const fertilizersArea = registeredArea(fertilizersAreaSlug, 'Düngemittel');
const yieldsArea = registeredArea(yieldsAreaSlug, 'Ertragsangaben');
const previousYearArea = registeredArea(previousYearAreaSlug, 'Vorjahresdaten');
const cultivationDataArea = registeredArea(cultivationDataAreaSlug, 'Anbaudaten');
const fertilizerPlanArea = registeredArea(fertilizerPlanAreaSlug, 'Düngeplanung');
const documentationArea = registeredArea(documentationAreaSlug, 'Dokumentation');
const nutrientComparisonArea = registeredArea(nutrientComparisonAreaSlug, 'Nährstoffvergleich');

/** Which area's screen a route addresses; an area it leaves out opens on its list. */
type RequestedAreaViews = Partial<IAreaViews>;

/**
 * Renders one work area of a fertilizer year. Every area route shares this
 * single `html` template literal so lit-html reuses the same element across
 * area navigation; only the `area` and `subArea` bindings update, which keeps the page's
 * validation Task on its cached value instead of refetching farm, year and
 * organisation on each area click.
 * @param area - The area to render.
 * @param params - The route parameters; `organizationId` is absent on the
 *     standalone farm routes, and every id read falls back to the empty string.
 * @param subArea - The sub-area slug below the area, or the empty string for
 *     the area's own address, which shows its first sub-area.
 * @param views - Which screen the route addresses, for whichever area owns it. Each
 *     defaults to that area's list.
 * @returns The page template.
 */
function renderFertilizerYearArea (
    area: IFertilizerYearArea,
    params: Record<string, string | undefined>,
    subArea = '',
    views: RequestedAreaViews = {}
): TemplateResult {
    const {
        parcel, soilSample, fertilizerProduct, previousYear, yieldSeries, cultivationData, fertilizerApplication,
        organicFertilizerDelivery, livestockInventory,
    } = {
        ...listAreaViews,
        ...views,
    };

    return html`
        <fm-fertilizer-year-detail
            organizationId=${params.organizationId ?? ''}
            farmId=${params.farmId ?? ''}
            year=${params.year ?? ''}
            subArea=${subArea}
            .area=${area}
            .parcelView=${parcel}
            .soilSampleView=${soilSample}
            .fertilizerProductView=${fertilizerProduct}
            .previousYearView=${previousYear}
            .yieldSeriesView=${yieldSeries}
            .cultivationDataView=${cultivationData}
            .fertilizerApplicationView=${fertilizerApplication}
            .organicFertilizerDeliveryView=${organicFertilizerDelivery}
            .livestockInventoryView=${livestockInventory}>
        </fm-fertilizer-year-detail>
    `;
}

/** The two scope families every fertilizer-year route exists in. */
const fertilizerYearScopes = [
    '/organizations/:organizationId/farms/:farmId/fertilizer-years/:year',
    '/farms/:farmId/fertilizer-years/:year',
];

/**
 * The sub-area address one segment below the open address.
 *
 * Read off the open path because the two scope families prefix the area differently. The
 * query and the fragment are dropped.
 * @param slug - The sub-area to descend into.
 * @returns The address to replace the open one with.
 */
function firstSubAreaAddress (slug: string): string {
    return `${window.location.pathname.replace(/\/$/u, '')}/${slug}`;
}

/**
 * The addresses one record's form is reached at: the record's own and the same with `/edit`.
 * Both render the one form; neither redirects.
 * @param suffix - What follows the area's address up to and including the record's id.
 * @returns The two suffixes.
 */
function formAddresses (suffix: string): Array<string> {
    return [
        suffix,
        `${suffix}/edit`,
    ];
}

/**
 * The routes hanging under the Schlagdaten: the create form, a Schlag's form (at its own
 * address and at the `/edit` alias), and the create form opened on that Schlag's values.
 *
 * All of them render the same year page as the area route, which is what keeps the year's
 * validation, its chrome and its write gate in one place; only the view changes.
 *
 * `new` is declared **before** `:parcelId`, because the router takes the first matching
 * route and `:parcelId` would otherwise swallow it.
 * @param area - The Schlagdaten area.
 * @returns The route configurations, one set per scope family.
 */
function parcelRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    const views: Array<{ suffix: string;
        title: string;
        view: (parcelId: string) => ParcelView; }> = [
        {
            suffix: '/new',
            title: 'Schlag anlegen',
            view: (): ParcelView => ({
                kind: 'create',
                lead: 'Legen Sie einen neuen Schlag im ausgewählten Düngejahr an.',
            }),
        },
        ...formAddresses('/:parcelId').map(suffix => ({
            suffix,
            title: 'Stammdaten des Schlags bearbeiten',
            view: (parcelId: string): ParcelView => ({
                kind: 'record',
                parcelId,
            }),
        })),

        // Under the Schlag it copies rather than beside `/new`, because the source is what
        // the screen is *of*: the address then names it, survives a reload and can be
        // shared, which a create form carrying its source in component state cannot.
        {
            suffix: '/:parcelId/duplicate',
            title: 'Schlag duplizieren',
            view: (parcelId): ParcelView => ({
                kind: 'duplicate',
                sourceParcelId: parcelId,

                // Says that nothing has happened yet as well as what the form is for: the
                // values on screen are a copy of another Schlag's and become one only on
                // „Anlegen“.
                lead: 'Die Werte des duplizierten Schlags sind übernommen. ' +
                    'Prüfen Sie sie und legen Sie den neuen Schlag an.',
            }),
        },
    ];

    return fertilizerYearScopes.flatMap(scope => views.map(({ suffix, title, view }) => ({
        path: `${scope}/${area.slug}${suffix}`,
        title,
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            '',
            {
                parcel: view(params.parcelId ?? ''),
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * The routes hanging under the Bodenuntersuchung: the form of one Schlag's Bodenprobe, at
 * its own address and at the `/edit` alias. There is no create address.
 * @param area - The Bodenuntersuchung area.
 * @returns The route configurations, one set per scope family.
 */
function soilSampleRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    return fertilizerYearScopes.flatMap(scope => formAddresses('/:parcelId').map(suffix => ({
        path: `${scope}/${area.slug}${suffix}`,
        title: 'Bodenprobe des Schlags bearbeiten',
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            '',
            {
                soilSample: {
                    kind: 'record',
                    parcelId: params.parcelId ?? '',
                },
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * One arm of the Düngemittel, as its routes differ: the sub-area its screens hang under,
 * the German noun of their titles, and the two view kinds.
 */
interface IFertilizerProductArm {
    subAreaSlug: string;
    noun: string;
    create: FertilizerProductView;
    record: (fertilizerProductId: string) => FertilizerProductView;
}

const fertilizerProductArms: ReadonlyArray<IFertilizerProductArm> = [
    {
        subAreaSlug: organicSubAreaSlug,
        noun: 'Wirtschaftsdünger',
        create: {
            kind: 'create',
            lead: 'Wählen Sie eine Vorlage aus dem Katalog. Ihre Werte werden übernommen und ' +
                'können vor dem Anlegen angepasst werden.',
        },
        record: (fertilizerProductId): FertilizerProductView => ({
            kind: 'record',
            fertilizerProductId,
        }),
    },
    {
        subAreaSlug: mineralSubAreaSlug,
        noun: 'Mineraldünger',
        create: {
            kind: 'mineralCreate',

            // Names the second way in as well, this arm's `Vorlage` being optional where the
            // organic one's is not.
            lead: 'Wählen Sie eine Vorlage aus dem Katalog oder erfassen Sie die Gehalte ' +
                'selbst. Übernommene Werte können vor dem Anlegen angepasst werden.',
        },
        record: (fertilizerProductId): FertilizerProductView => ({
            kind: 'mineralRecord',
            fertilizerProductId,
        }),
    },
];

/**
 * The screens one arm hangs under its sub-area: the create form, and a Düngemittel's form
 * at its own address and at the `/edit` alias.
 *
 * All of them render the year page of the area route; only the view changes. Each names
 * its sub-area, so a non-writer's fallback lands on the tab the record belongs to.
 *
 * **Both arms hang under a literal sub-area segment**, so `:fertilizerProductId` never
 * sits directly under the area slug and no address matches two of these patterns. The one
 * ordering left is `new` before `:fertilizerProductId`, for the reason `parcelRoutes`
 * states.
 * @param area - The Düngemittel area.
 * @param arm - The arm to build routes for.
 * @returns The route configurations, one set per scope family.
 */
function fertilizerProductArmRoutes (area: IFertilizerYearArea, arm: IFertilizerProductArm): Array<IAppRoute> {
    const views: Array<{ suffix: string;
        title: string;
        view: (fertilizerProductId: string) => FertilizerProductView; }> = [
        {
            suffix: '/new',
            title: `${arm.noun} anlegen`,
            view: (): FertilizerProductView => arm.create,
        },
        ...formAddresses('/:fertilizerProductId').map(suffix => ({
            suffix,
            title: `${arm.noun} bearbeiten`,
            view: arm.record,
        })),
    ];

    return fertilizerYearScopes.flatMap(scope => views.map(({ suffix, title, view }) => ({
        path: `${scope}/${area.slug}/${arm.subAreaSlug}${suffix}`,
        title,
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            arm.subAreaSlug,
            {
                fertilizerProduct: view(params.fertilizerProductId ?? ''),
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * The screens of each Düngemittel arm, below the arm's own sub-area.
 * @param area - The Düngemittel area.
 * @returns The route configurations, one set per scope family.
 */
function fertilizerProductRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    return fertilizerProductArms.flatMap(arm => fertilizerProductArmRoutes(area, arm));
}

/**
 * One tab of the Vorjahresdaten with records of its own: the sub-area its screens hang
 * below, which is also the page's leaf discriminator, and their document title.
 */
interface IPreviousYearTab {
    slug: PreviousYearTab;
    title: string;
}

/** The two parcel-scoped tabs of the Vorjahresdaten, in the strip's order. */
const previousYearTabs: ReadonlyArray<IPreviousYearTab> = [
    {
        slug: previousCropsSubAreaSlug,
        title: 'Vorjahreskultur des Schlags bearbeiten',
    },
    {
        slug: organicFertilizationSubAreaSlug,
        title: 'Organische Düngung des Schlags bearbeiten',
    },
];

/**
 * Below each of the two Vorjahresdaten tabs, the form of one Schlag's record, at its own
 * address and at the `/edit` alias. There is no create address.
 *
 * They hang below the **tab's** segment rather than the area's, which is what
 * `addressesEverySubArea` on the registry entry is for: under the area's own segment a
 * `:parcelId` pattern would sit where it can swallow the tab beside it, and which of the two
 * answered would depend on registration order.
 *
 * All of them render the year page of the area route; only the view changes.
 * @param area - The Vorjahresdaten area.
 * @returns The route configurations, one set per scope family.
 */
function previousYearRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    return fertilizerYearScopes.flatMap(scope => previousYearTabs.flatMap(
        tab => formAddresses('/:parcelId').map(suffix => ({
            path: `${scope}/${area.slug}/${tab.slug}${suffix}`,
            title: tab.title,
            render: (params): TemplateResult => renderFertilizerYearArea(
                area,
                params,
                '',
                {
                    previousYear: {
                        kind: 'record',
                        tab: tab.slug,
                        parcelId: params.parcelId ?? '',
                    },
                }
            ),
            enter: requireAuth,
        }))
    ));
}

/**
 * The Anbaudaten's record routes: the form of one Kultur, at its own address and at the
 * `/edit` alias.
 *
 * The Kultur hangs **two** segments below the area, its Schlag standing in the address before
 * it: every call below a Kultur names the Schlag too, so a deep link that carried the Kultur
 * alone could not read it back. The area carries no sub-areas, so nothing literal sits where
 * a `:parcelId` pattern could swallow it.
 *
 * Both render the same year page as the area route, which is what keeps the year's
 * validation, its chrome and its write gate in one place; only the view changes.
 * @param area - The Anbaudaten area.
 * @returns The route configurations, one pair per scope family.
 */
function cultivationDataRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    return fertilizerYearScopes.flatMap(scope => formAddresses('/:parcelId/:parcelCropId').map(suffix => ({
        path: `${scope}/${area.slug}${suffix}`,
        title: 'Kultur bearbeiten',
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            '',
            {
                cultivationData: {
                    kind: 'record',
                    parcelId: params.parcelId ?? '',
                    parcelCropId: params.parcelCropId ?? '',
                },
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * The pages of a mineral dose below the mineral tab of the Düngeplanung or the Dokumentation:
 * the one that adds a dose to a Kultur, and a dose's form (at its own address and at the
 * `/edit` alias).
 *
 * **A dose is addressed under its Kultur, and the Kultur under its Schlag**, because every
 * call below a Kultur names the Schlag too. `new` sits at the depth of the dose's id, so it is
 * registered first. The organic tab has no such routes: its doses edit in dialogs.
 * @param area - The Düngeplanung or the Dokumentation.
 * @returns The route configurations, one set per scope family.
 */
function fertilizerApplicationRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    const cropPath = `/${mineralFertilizationSubAreaSlug}/:parcelId/:parcelCropId`;
    const addTitle = area.slug === documentationAreaSlug ? 'Gabe dokumentieren' : 'Gabe planen';
    const views: Array<{ suffix: string;
        title: string;
        view: (params: Record<string, string | undefined>) => FertilizerApplicationView; }> = [
        {
            suffix: `${cropPath}/new`,
            title: addTitle,
            view: (params): FertilizerApplicationView => ({
                kind: 'create',
                parcelId: params.parcelId ?? '',
                parcelCropId: params.parcelCropId ?? '',
            }),
        },
        ...formAddresses(`${cropPath}/:fertilizerApplicationId`).map(suffix => ({
            suffix,
            title: 'Gabe bearbeiten',
            view: (params: Record<string, string | undefined>): FertilizerApplicationView => ({
                kind: 'record',
                parcelId: params.parcelId ?? '',
                parcelCropId: params.parcelCropId ?? '',
                fertilizerApplicationId: params.fertilizerApplicationId ?? '',
            }),
        })),
    ];

    return fertilizerYearScopes.flatMap(scope => views.map(({ suffix, title, view }) => ({
        path: `${scope}/${area.slug}${suffix}`,
        title,
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            mineralFertilizationSubAreaSlug,
            {
                fertilizerApplication: view(params),
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * The form pages of the Nährstoffvergleich's Organische Dünger, below the tab's own segment:
 * the one that records a delivery, and a delivery's form at its own address and at the
 * `/edit` alias.
 *
 * They hang below the **tab's** segment rather than the area's, which is what
 * `addressesEverySubArea` on the registry entry is for; `new` is declared before
 * `:organicFertilizerDeliveryId` for the reason `parcelRoutes` states.
 * @param area - The Nährstoffvergleich area.
 * @returns The route configurations, one set per scope family.
 */
function organicFertilizerDeliveryRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    const views: Array<{ suffix: string;
        title: string;
        view: (organicFertilizerDeliveryId: string) => OrganicFertilizerDeliveryView; }> = [
        {
            suffix: '/new',
            title: 'Lieferung erfassen',
            view: (): OrganicFertilizerDeliveryView => ({
                kind: 'create',
            }),
        },
        ...formAddresses('/:organicFertilizerDeliveryId').map(suffix => ({
            suffix,
            title: 'Lieferung bearbeiten',
            view: (organicFertilizerDeliveryId: string): OrganicFertilizerDeliveryView => ({
                kind: 'record',
                organicFertilizerDeliveryId,
            }),
        })),
    ];

    return fertilizerYearScopes.flatMap(scope => views.map(({ suffix, title, view }) => ({
        path: `${scope}/${area.slug}/${organicFertilizersSubAreaSlug}${suffix}`,
        title,
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            organicFertilizersSubAreaSlug,
            {
                organicFertilizerDelivery: view(params.organicFertilizerDeliveryId ?? ''),
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * The form page of one Tierbestand row, at its own address and the `/edit` alias. There is no
 * create address: rows come from the catalog picker over the list, several at once.
 *
 * It hangs below the **tab's** segment for the reason {@link organicFertilizerDeliveryRoutes}
 * gives.
 * @param area - The Nährstoffvergleich area.
 * @returns The route configurations, one set per scope family.
 */
function livestockInventoryRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    return fertilizerYearScopes.flatMap(scope => formAddresses('/:livestockInventoryEntryId').map(suffix => ({
        path: `${scope}/${area.slug}/${livestockSubAreaSlug}${suffix}`,
        title: 'Tierzeile bearbeiten',
        render: (params): TemplateResult => renderFertilizerYearArea(
            area,
            params,
            livestockSubAreaSlug,
            {
                livestockInventory: {
                    kind: 'record',
                    livestockInventoryEntryId: params.livestockInventoryEntryId ?? '',
                },
            }
        ),
        enter: requireAuth,
    })));
}

/**
 * The route at an area's **own** address: the area itself for the ordinary reading, and a
 * redirect onto the first tab for an area declaring `addressesEverySubArea`.
 *
 * Every tab of such an area is addressed by the generator, which leaves the area's own
 * address rendering nothing. The redirect settles it on the first tab, so each view keeps one
 * canonical address, and it **replaces** the history entry rather than pushing one, which
 * would make the back button lead straight forward again. It lives here rather than with an
 * area's own routes, every such area owing the same one.
 * @param area - The area to build the route for.
 * @param scope - The area's address under one scope family.
 * @returns The route, or nothing where the area declares tabs it does not have.
 */
function areaOwnRoute (area: IFertilizerYearArea, scope: string): Array<IAppRoute> {
    const path = `${scope}/${area.slug}`;

    if (area.addressesEverySubArea !== true) {
        return [
            {
                path,
                title: area.label,
                render: (params): TemplateResult => renderFertilizerYearArea(area, params),
                enter: requireAuth,
            },
        ];
    }

    const firstSubArea = area.subAreas?.at(0);

    if (firstSubArea === undefined) {
        return [];
    }

    return [
        {
            path,
            title: null,
            render: (): TemplateResult => html``,
            enter: async (): Promise<boolean> => {
                if (await requireAuth()) {
                    redirectToReplacing(firstSubAreaAddress(firstSubArea.slug));
                }

                return false;
            },
        },
    ];
}

/** The address released versions gave the N-Anpassung N-Kulisse, kept so a saved link still arrives. */
const formerNitrogenAdjustmentSlug = 'fertilizer-requirement';

/**
 * The N-Anpassung N-Kulisse's former address in each scope family, replacing itself with the area's
 * own, as {@link areaOwnRoute} does for an area's first tab.
 * @returns The routes.
 */
function formerNitrogenAdjustmentRoutes (): Array<IAppRoute> {
    return fertilizerYearScopes.map((scope): IAppRoute => ({
        path: `${scope}/${formerNitrogenAdjustmentSlug}`,
        title: null,
        render: (): TemplateResult => html``,
        enter: async (): Promise<boolean> => {
            if (await requireAuth()) {
                redirectToReplacing(window.location.pathname.replace(
                    /\/[^/]+\/?$/u,
                    `/${nitrogenAdjustmentAreaSlug}`
                ));
            }

            return false;
        },
    }));
}

/**
 * The routes of one work area, one per scope family (organisation-scoped and
 * standalone), mirroring the year routes they hang below. Generated from the
 * area registry so an added area brings its routes with it; a slug outside the
 * registry keeps falling through to the 404 fallback.
 *
 * A sub-area brings one route per scope family the same way, except the first
 * one of an area: that one is shown at the area's own address, so no two
 * addresses render the same view. An area declaring `addressesEverySubArea` is
 * the other reading, and brings the first one too ({@link areaOwnRoute}).
 *
 * An area's title is its own registry label, so an added area names its document
 * without a second decision being taken anywhere. A sub-area's names its area in
 * front of it, a sub-area label being unique only within its area:
 * `Mineralische Düngung` is both a fertilizer plan and a documentation view.
 * @param area - The area to build routes for.
 * @returns The area's route configurations.
 */
function fertilizerYearAreaRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    const addressed = area.addressesEverySubArea === true;
    const subAreas = area.subAreas ?? [];

    return fertilizerYearScopes.flatMap(scope => [
        ...areaOwnRoute(area, scope),
        ...(addressed ? subAreas : subAreas.slice(1)).map((sub): IAppRoute => ({
            path: `${scope}/${area.slug}/${sub.slug}`,
            title: `${area.label}: ${sub.label}`,
            render: (params): TemplateResult => renderFertilizerYearArea(area, params, sub.slug),
            enter: requireAuth,
        })),
    ]);
}

/**
 * The address one Ertragsangaben tab's records hang under, which mirrors `subAreaPath` in
 * the year page: the area's own address for the first tab, which is shown there rather
 * than at a segment of its own, and one segment below the area for the second.
 *
 * The asymmetry is the area's declared shape rather than a rule of its own. The
 * Ertragsangaben do not declare `addressesEverySubArea`, so the first tab has no address
 * besides the area's, and hanging its records anywhere else would give that tab's list two
 * addresses.
 * @param scope - The year's address under one scope family.
 * @param area - The Ertragsangaben area.
 * @param inNitrateVulnerableZone - Which of the two tabs is wanted.
 * @returns The address the tab's records hang under.
 */
function yieldArmPath (scope: string, area: IFertilizerYearArea, inNitrateVulnerableZone: boolean): string {
    const slug = yieldSubAreaSlug(inNitrateVulnerableZone);
    const areaPath = `${scope}/${area.slug}`;

    return slug === area.slug ? areaPath : `${areaPath}/${slug}`;
}

/**
 * The Ertragsreihen screens of **each** tab, the create page and a series' form (at its own
 * address and at the `/edit` alias), below the address that tab is reached at
 * ({@link yieldArmPath}). The window a screen renders and the flag a create writes are the
 * arm's, so the address states them.
 *
 * The first tab's set puts `:yieldSeriesId` at the same depth as the literal
 * `nitrate-vulnerable-zone` segment beside it, so the two would both match one address.
 * The literal wins because `fertilizerYearAreaRoutes` registers the tab first and the
 * router answers in registration order; the same reasoning puts `new` before
 * `:yieldSeriesId` here, and the N-Kulisse arm before the ordinary one.
 * @param area - The Ertragsangaben area.
 * @returns The route configurations, one set per arm per scope family.
 */
function yieldSeriesRoutes (area: IFertilizerYearArea): Array<IAppRoute> {
    const views: Array<{ suffix: string;
        title: string;
        view: (yieldSeriesId: string, inNitrateVulnerableZone: boolean) => YieldSeriesView; }> = [
        {
            suffix: '/new',
            title: 'Kultur hinzufügen',
            view: (_yieldSeriesId, inNitrateVulnerableZone): YieldSeriesView => ({
                kind: 'create',
                lead: 'Legen Sie eine neue Ertragsreihe im ausgewählten Düngejahr an.',
                inNitrateVulnerableZone,
            }),
        },
        ...formAddresses('/:yieldSeriesId').map(suffix => ({
            suffix,
            title: 'Ertragsreihe bearbeiten',
            view: (yieldSeriesId: string, inNitrateVulnerableZone: boolean): YieldSeriesView => ({
                kind: 'record',
                yieldSeriesId,
                inNitrateVulnerableZone,
            }),
        })),
    ];

    const armFlags = [
        true,
        false,
    ];

    return fertilizerYearScopes.flatMap(scope => armFlags.flatMap(
        inNitrateVulnerableZone => views.map(({ suffix, title, view }) => ({
            path: `${yieldArmPath(scope, area, inNitrateVulnerableZone)}${suffix}`,
            title,
            render: (params): TemplateResult => renderFertilizerYearArea(
                area,
                params,
                yieldSubAreaSlug(inNitrateVulnerableZone),
                {
                    yieldSeries: view(params.yieldSeriesId ?? '', inNitrateVulnerableZone),
                }
            ),
            enter: requireAuth,
        }))
    ));
}

@customElement('fm-app')
export class FmApp extends LitElement {

    static styles = css`
        :host {
            display: block;
        }
    `;

    @state()
    private sessionConfirmed = false;

    /**
     * Every route, each naming the view it renders.
     *
     * Held as its own field so the literal is checked against {@link IAppRoute}: a
     * route added without a title then fails to compile rather than joining the ones
     * that do name themselves.
     */
    private readonly routes: Array<IAppRoute> = [
        {
            path: '/',
            title: null,
            render: (): TemplateResult => html``,
            enter: enterDefaultPath,
        },
        {
            path: '/dashboard',
            title: null,
            render: (): TemplateResult => html``,
            enter: enterDefaultPath,
        },
        {
            path: '/home',
            title: 'Willkommen',
            render: (): TemplateResult => html`<fm-home></fm-home>`,
            enter: requireAuth,
        },
        {
            path: '/organizations',
            title: 'Organisationen',
            render: (): TemplateResult => html`<fm-organization-list></fm-organization-list>`,
            enter: requireAuth,
        },
        {
            path: '/organizations/new',
            title: 'Organisation anlegen',
            render: (): TemplateResult => html`<fm-organization-create></fm-organization-create>`,
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId',
            title: 'Stammdaten der Organisation',
            render: (params): TemplateResult => renderOrganizationDetail(params.organizationId ?? '', 'master-data'),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/edit',
            title: 'Stammdaten der Organisation bearbeiten',
            render: (params): TemplateResult => renderOrganizationDetail(
                params.organizationId ?? '',
                'master-data',
                'edit'
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms',
            title: 'Betriebe der Organisation',
            render: (params): TemplateResult => renderOrganizationDetail(params.organizationId ?? '', 'farms'),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/transfers',
            title: 'Umzüge der Organisation',
            render: (params): TemplateResult => renderOrganizationDetail(params.organizationId ?? '', 'transfers'),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/invitations',
            title: 'Einladungen der Organisation',
            render: (params): TemplateResult => renderOrganizationDetail(
                params.organizationId ?? '',
                'invitations'
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/organization-access',
            title: 'Organisationszugriff',
            render: (params): TemplateResult => renderOrganizationDetail(
                params.organizationId ?? '',
                'organization-access'
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farm-access',
            title: 'Betriebszugriff der Organisation',
            render: (params): TemplateResult => renderOrganizationDetail(
                params.organizationId ?? '',
                'farm-access'
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/new',
            title: 'Betrieb anlegen',
            render: (params): TemplateResult => html`
                <fm-farm-create organizationId=${params.organizationId ?? ''}></fm-farm-create>
            `,
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId',
            title: 'Stammdaten des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'master-data',
                params.organizationId ?? ''
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/edit',
            title: 'Stammdaten des Betriebs bearbeiten',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'master-data',
                params.organizationId ?? '',
                'edit'
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/fertilizer-years',
            title: 'Düngejahre des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'fertilizer-years',
                params.organizationId ?? ''
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/fertilizer-years/:year',
            title: (params): string => `Düngejahr ${params.year ?? ''}`,
            render: (params): TemplateResult => html`
                <fm-fertilizer-year-detail
                    organizationId=${params.organizationId ?? ''}
                    farmId=${params.farmId ?? ''}
                    year=${params.year ?? ''}
                    .area=${null}>
                </fm-fertilizer-year-detail>
            `,
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/invitations',
            title: 'Einladungen des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'invitations',
                params.organizationId ?? ''
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/farm-access',
            title: 'Betriebszugriff',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'farm-access',
                params.organizationId ?? ''
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/transfer',
            title: 'Umzug des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'transfer',
                params.organizationId ?? ''
            ),
            enter: requireAuth,
        },
        {
            path: '/organizations/:organizationId/farms/:farmId/detach',
            title: 'Auszug des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(
                params.farmId ?? '',
                'detach',
                params.organizationId ?? ''
            ),
            enter: requireAuth,
        },
        {
            path: '/farms',
            title: 'Betriebe',
            render: (): TemplateResult => html`<fm-farm-list></fm-farm-list>`,
            enter: requireAuth,
        },
        {
            path: '/farms/new',
            title: 'Betrieb anlegen',
            render: (): TemplateResult => html`<fm-farm-create></fm-farm-create>`,
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId',
            title: 'Stammdaten des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'master-data', ''),
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/edit',
            title: 'Stammdaten des Betriebs bearbeiten',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'master-data', '', 'edit'),
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/fertilizer-years',
            title: 'Düngejahre des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'fertilizer-years', ''),
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/fertilizer-years/:year',
            title: (params): string => `Düngejahr ${params.year ?? ''}`,
            render: (params): TemplateResult => html`
                <fm-fertilizer-year-detail
                    farmId=${params.farmId ?? ''}
                    year=${params.year ?? ''}
                    .area=${null}>
                </fm-fertilizer-year-detail>
            `,
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/invitations',
            title: 'Einladungen des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'invitations', ''),
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/farm-access',
            title: 'Betriebszugriff',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'farm-access', ''),
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/transfer',
            title: 'Umzug des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'transfer', ''),
            enter: requireAuth,
        },
        {
            path: '/farms/:farmId/detach',
            title: 'Auszug des Betriebs',
            render: (params): TemplateResult => renderFarmDetail(params.farmId ?? '', 'detach', ''),
            enter: requireAuth,
        },
        ...fertilizerYearAreas.flatMap(fertilizerYearAreaRoutes),
        ...formerNitrogenAdjustmentRoutes(),
        ...parcelRoutes(parcelsArea),
        ...soilSampleRoutes(soilSamplesArea),
        ...fertilizerProductRoutes(fertilizersArea),
        ...previousYearRoutes(previousYearArea),
        ...yieldSeriesRoutes(yieldsArea),
        ...cultivationDataRoutes(cultivationDataArea),
        ...fertilizerApplicationRoutes(fertilizerPlanArea),
        ...fertilizerApplicationRoutes(documentationArea),
        ...organicFertilizerDeliveryRoutes(nutrientComparisonArea),
        ...livestockInventoryRoutes(nutrientComparisonArea),
        {
            path: '/login',
            title: 'Anmeldung',
            render: (): TemplateResult => html`<fm-login></fm-login>`,
            enter: requireGuest,
        },
        {
            path: '/login/verified',
            title: 'Anmeldung',
            render: (): TemplateResult => html`<fm-login hint="verified"></fm-login>`,
            enter: requireGuest,
        },
        {
            path: '/login/already-verified',
            title: 'Anmeldung',
            render: (): TemplateResult => html`<fm-login hint="alreadyVerified"></fm-login>`,
            enter: requireGuest,
        },
        {
            path: '/login/email-changed',
            title: 'Anmeldung',
            render: (): TemplateResult => html`<fm-login hint="emailChanged"></fm-login>`,
            enter: requireGuest,
        },
        {
            path: '/registration',
            title: 'Registrierung',
            render: (): TemplateResult => html`<fm-registration></fm-registration>`,
            enter: enterPublicRoute,
        },
        {
            path: '/registration/link-expired',
            title: 'Verifizierungslink abgelaufen',
            render: (): TemplateResult => html`<fm-registration-error reason="expired"></fm-registration-error>`,
            enter: enterPublicRoute,
        },
        {
            path: '/registration/link-invalid',
            title: 'Verifizierungslink ungültig',
            render: (): TemplateResult => html`<fm-registration-error reason="invalid"></fm-registration-error>`,
            enter: enterPublicRoute,
        },
        {
            path: '/registration/link-used',
            title: 'Verifizierungslink bereits verwendet',
            render: (): TemplateResult => html`<fm-registration-error reason="used"></fm-registration-error>`,
            enter: enterPublicRoute,
        },
        {
            path: '/registration/verification-failed',
            title: 'E-Mail-Verifizierung fehlgeschlagen',
            render: (): TemplateResult => html`<fm-registration-error reason="serviceError"></fm-registration-error>`,
            enter: enterPublicRoute,
        },
        {
            path: '/reset-password',
            title: 'Passwort zurücksetzen',
            render: (): TemplateResult => html`<fm-password-reset-request></fm-password-reset-request>`,
            enter: requireGuest,
        },
        {
            path: '/new-password',
            title: 'Neues Passwort festlegen',
            render: (): TemplateResult => html`<fm-password-reset-form></fm-password-reset-form>`,
            enter: enterPublicRoute,
        },
        {
            path: '/account',
            title: 'Mein Konto',
            render: (): TemplateResult => renderAccount('view'),
            enter: requireAuth,
        },
        {
            path: '/account/personal-data/edit',
            title: 'Persönliche Daten bearbeiten',
            render: (): TemplateResult => renderAccount('edit-personal-data'),
            enter: requireAuth,
        },
        {
            path: '/account/email/edit',
            title: 'E-Mail-Adresse bearbeiten',
            render: (): TemplateResult => renderAccount('edit-email'),
            enter: requireAuth,
        },
        {
            path: '/account/invitations',
            title: null,
            render: (): TemplateResult => html``,
            enter: async (): Promise<boolean> => {
                if (await requireAuth()) {
                    redirectTo('/invitations');
                }

                return false;
            },
        },
        {
            path: '/invitations',
            title: 'Einladungen',
            render: (): TemplateResult => html`<fm-invitations></fm-invitations>`,
            enter: requireAuth,
        },
        {
            path: '/settings',
            title: 'Einstellungen',
            render: (): TemplateResult => html`<fm-settings></fm-settings>`,
            enter: requireAuth,
        },
        {
            path: '/administration',
            title: 'Einladungen der Verwaltung',
            render: (): TemplateResult => renderAdmin('invitations'),
            enter: requireAdmin,
        },
        {
            path: '/administration/access',
            title: 'Verwaltungszugriff',
            render: (): TemplateResult => renderAdmin('accounts'),
            enter: requireAdmin,
        },
        {
            path: '/api-docs',
            title: 'API-Dokumentation',
            render: (): TemplateResult => html`<fm-api-docs></fm-api-docs>`,
            enter: requireApiDocs,
        },
    ];

    private readonly router = new Router(this, this.routes.map(titled), {
        fallback: {
            render: (): TemplateResult => {
                if (isAuthenticated()) {
                    return html`<fm-not-found-authenticated></fm-not-found-authenticated>`;
                }

                return html`<fm-not-found></fm-not-found>`;
            },
            enter: async (): Promise<boolean> => {

                // Refresh the cached auth state so `render()` picks the
                // correct shell (public vs. application frame) AND the
                // correct 404 component. Swallow network errors; the
                // public 404 should still render in that case.
                try {
                    await refreshAuthentication();
                } catch {

                    // Treat as unauthenticated; the public 404 renders.
                }

                if (isAuthenticated()) {
                    await loadApplicationFrameBundle();
                } else {
                    await loadPublicShellBundle();
                }

                // Named here rather than through `titled`, the fallback carrying no
                // path for a declaration to hang a title on. Both 404s it chooses
                // between are the same view as far as a title is concerned.
                setDocumentTitle('Seite nicht gefunden');

                return true;
            },
        },
    });

    override connectedCallback (): void {
        super.connectedCallback();
        void this.bootstrapSession();
    }

    /**
     * Confirms the session with the server before the authenticated shell is
     * committed, so a token that is locally valid but server-invalid (e.g.
     * after a key rotation) does not flash the application frame. Only the
     * application-frame case needs confirming: public paths render the public
     * shell regardless, and a locally expired token is confirmed by the route
     * guard's own server refresh. A definitive server rejection ends the
     * session via `authenticatedFetch`; a transient network error resolves
     * optimistically. The check reads the display preferences, which the shell needs next.
     */
    private async bootstrapSession (): Promise<void> {
        const path = window.location.pathname;

        if (!publicPaths.has(path) && isAuthenticated()) {
            await hydrateDisplaySettings();
        }

        this.sessionConfirmed = true;
    }

    /**
     * Wraps the active route's outlet in either the public shell or the
     * authenticated application frame. Known public routes always use the
     * public shell. Unknown paths (404 fallback) use the public shell when
     * the user is not authenticated and the application frame when they
     * are, so the 404 experience matches the surrounding chrome of the
     * user's session state. All other paths use the application frame;
     * unauthenticated users on those paths are caught by `requireAuth`
     * and redirected to `/login` before this shell is committed to view.
     *
     * While the session is being confirmed on a protected path, an empty
     * template is rendered instead of the application frame, so a token that
     * is locally valid but server-invalid does not flash the shell before the
     * server-side confirmation completes.
     *
     * Keeping the shell choice at this level — rather than wrapping each
     * route's render — means the chosen shell instance is reused across
     * all navigations within its half of the app. `<fm-application-frame>`
     * therefore stays mounted across tab navigation, preserving its
     * sidebar state and avoiding a full re-fetch of the organization list
     * on every URL change.
     * @returns The shell wrapping the route outlet.
     */
    render (): TemplateResult {
        const path = window.location.pathname;
        const isPublic = publicPaths.has(path);

        if (!isPublic && isAuthenticated() && !this.sessionConfirmed) {
            return html``;
        }

        const usePublicShell = isPublic || !isAuthenticated();

        return usePublicShell ?
            html`<fm-public-shell>${this.router.outlet()}</fm-public-shell>` :
            html`<fm-application-frame>${this.router.outlet()}</fm-application-frame>`;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'fm-app': FmApp;
    }
}
